import type { LanguageCode, PublicInterviewInfo, RoomState, ServerMessage } from '@eduvy/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, cx } from '../components/ui';
import { ClipPlayer, MicCapture } from './audio';

type Phase = 'landing' | 'connecting' | 'live' | 'ended' | 'error';

interface Caption {
  who: 'ai' | 'you';
  text: string;
  turn: number;
}

/**
 * The built-in (WebSocket) interview room, used when the server runs without LiveKit.
 * No account, no install: open the link, check the mic, agree, talk.
 */
export function BuiltinRoom({ token, info }: { token: string; info: PublicInterviewInfo }) {
  const [phase, setPhase] = useState<Phase>('landing');
  const [message, setMessage] = useState<string>('');
  const [language, setLanguage] = useState<LanguageCode>(info.languages[0]?.code ?? 'en-IN');
  const [agreed, setAgreed] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [roomState, setRoomState] = useState<RoomState>('connecting');
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [muted, setMuted] = useState(false);
  const [aiPlaying, setAiPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [draft, setDraft] = useState('');

  const ws = useRef<WebSocket | null>(null);
  const mic = useRef<MicCapture | null>(null);
  const player = useRef<ClipPlayer | null>(null);
  const startedAt = useRef<number>(0);
  const ended = useRef(false);


  // Mic level meter (landing mic check, and the live "listening" ring).
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (mic.current) setLevel(mic.current.level());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (phase !== 'live') return;
    const t = setInterval(() => setElapsed(Date.now() - startedAt.current), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const send = useCallback((msg: object) => {
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify(msg));
  }, []);

  // Integrity signals: the hiring team sees them; nothing is blocked.
  useEffect(() => {
    if (phase !== 'live') return;
    const vis = () => send({ type: 'integrity', event: document.hidden ? 'tab_hidden' : 'tab_visible' });
    const blur = () => send({ type: 'integrity', event: 'window_blur' });
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('blur', blur);
    return () => {
      document.removeEventListener('visibilitychange', vis);
      window.removeEventListener('blur', blur);
    };
  }, [phase, send]);

  useEffect(() => () => teardown(), []);

  function teardown() {
    mic.current?.stop();
    mic.current = null;
    player.current?.clear();
    ws.current?.close();
    ws.current = null;
  }

  async function checkMic() {
    setMicError(null);
    try {
      if (!mic.current) {
        mic.current = await MicCapture.start((frame) => {
          if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(frame.buffer as ArrayBuffer);
        });
      }
    } catch (err) {
      setMicError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow it from the address bar, then try again.'
          : 'We could not open a microphone on this device.',
      );
    }
  }

  async function begin(asText: boolean) {
    if (!asText) {
      await checkMic();
      if (!mic.current) return;
    }
    setTextMode(asText);
    const p = new ClipPlayer((id) => send({ type: 'played', clip: id }), setAiPlaying);
    p.unlock();
    player.current = p;
    setPhase('connecting');
    connect(asText);
  }

  function connect(asText: boolean) {
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/interview/${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    ws.current = socket;
    let ping: ReturnType<typeof setInterval> | undefined;
    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'start', language, textMode: asText }));
      startedAt.current = Date.now();
      setPhase('live');
      ping = setInterval(() => socket.readyState === WebSocket.OPEN && socket.send(JSON.stringify({ type: 'ping' })), 20_000);
    };
    socket.onmessage = (e) => {
      if (e.data instanceof ArrayBuffer) {
        const id = new DataView(e.data).getUint32(0, true);
        player.current?.enqueue(id, e.data.slice(4));
        return;
      }
      const msg = JSON.parse(e.data as string) as ServerMessage;
      switch (msg.type) {
        case 'state':
          setRoomState(msg.state);
          break;
        case 'ai_text':
          setCaptions((c) => mergeCaption(c, { who: 'ai', text: msg.text, turn: msg.turn }));
          break;
        case 'candidate_text':
          setCaptions((c) => [...c, { who: 'you' as const, text: msg.text, turn: msg.turn }].slice(-12));
          break;
        case 'clear':
          player.current?.clear();
          break;
        case 'ended':
          ended.current = true;
          setMessage('Thank you! Your interview is complete. The hiring team will review it and get back to you.');
          break;
        case 'error':
          setMessage(msg.message);
          break;
      }
    };
    socket.onclose = (ev) => {
      clearInterval(ping);
      if (ev.code === 1008) {
        // Refused (expired, completed, invalid): the server already said why.
        teardown();
        setMessage((m) => m || 'This interview link is no longer active.');
        setPhase('error');
        return;
      }
      if (ended.current) {
        // Let the goodbye finish playing before tearing down.
        const wait = () => (player.current?.playing ? setTimeout(wait, 300) : (teardown(), setPhase('ended')));
        wait();
        return;
      }
      // A dropped connection: the server keeps the interview open for a minute.
      if (ws.current === socket) {
        setRoomState('connecting');
        setTimeout(() => ws.current === socket && connect(asText), 1500);
      }
    };
  }

  function endInterview() {
    if (!window.confirm('End the interview now? You will not be able to continue it.')) return;
    ended.current = true;
    send({ type: 'end' });
    setMessage('You ended the interview. Thank you for your time.');
  }

  function submitText() {
    const text = draft.trim();
    if (!text) return;
    send({ type: 'text', text });
    setDraft('');
  }

  if (phase === 'error' || phase === 'ended') {
    return (
      <Shell>
        <div className="py-16 text-center">
          <div className={cx('mx-auto flex h-14 w-14 items-center justify-center rounded-full text-2xl', phase === 'ended' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
            {phase === 'ended' ? '✓' : '!'}
          </div>
          <p className="mx-auto mt-5 max-w-md text-lg text-slate-800">{message || 'Thank you!'}</p>
          {phase === 'ended' && <p className="mt-2 text-sm text-slate-500">You can close this window.</p>}
        </div>
      </Shell>
    );
  }

  if (phase === 'landing') {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold text-slate-900">Hi {info.candidateFirstName} 👋</h1>
        <p className="mt-2 text-slate-600">
          Welcome to your first-round interview for <strong>{info.jobTitle}</strong> at <strong>{info.company}</strong>.
        </p>
        <ul className="mt-6 space-y-3 text-sm text-slate-700">
          <Tip icon="🎙️">
            It's a spoken conversation of about <strong>{info.minutes} minutes</strong> with {info.interviewerName}, our AI interviewer. Answer naturally, as you would on a call.
          </Tip>
          <Tip icon="🔇">Find a quiet place. Earphones help the interviewer hear you clearly.</Tip>
          <Tip icon="🗣️">You can speak in the language you're most comfortable with and switch at any time.</Tip>
          <Tip icon="👥">The conversation is recorded, and {info.company}'s hiring team reviews every interview and makes all decisions.</Tip>
        </ul>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Preferred language</span>
            <select
              className="block w-full rounded-lg border-0 px-3 py-2.5 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-brand-600"
              value={language}
              onChange={(e) => setLanguage(e.target.value as LanguageCode)}
            >
              {info.languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.native === l.label ? l.label : `${l.native} (${l.label})`}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm">
            <span className="mb-1 block font-medium text-slate-700">Microphone</span>
            {mic.current ? (
              <div className="flex h-[42px] items-center gap-3 rounded-lg bg-slate-50 px-3 ring-1 ring-slate-200">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
                </div>
                <span className="text-xs text-slate-500">Say something</span>
              </div>
            ) : (
              <Button className="w-full py-2.5" onClick={checkMic}>
                Test my microphone
              </Button>
            )}
            {micError && <p className="mt-1 text-xs text-rose-600">{micError}</p>}
          </div>
        </div>

        <label className="mt-6 flex items-start gap-3 rounded-lg bg-slate-50 p-4 text-sm ring-1 ring-slate-200">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand-700" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className="text-slate-700">
            I understand this interview is conducted by an AI, that it is recorded and assessed, and that {info.company}'s hiring team will review it. I agree to continue.
          </span>
        </label>

        <Button variant="primary" size="lg" className="mt-6 w-full" disabled={!agreed} onClick={() => begin(false)}>
          Start my interview
        </Button>
        {micError && (
          <button className="mt-3 w-full text-center text-sm text-slate-500 underline" disabled={!agreed} onClick={() => begin(true)}>
            My microphone isn't working, let me type my answers instead
          </button>
        )}
      </Shell>
    );
  }

  // Live.
  const lastAi = [...captions].reverse().find((c) => c.who === 'ai');
  const lastYou = [...captions].reverse().find((c) => c.who === 'you');
  const speaking = aiPlaying || roomState === 'speaking';
  const listening = !speaking && roomState === 'listening';
  return (
    <div className="flex min-h-full flex-col bg-gradient-to-b from-slate-900 to-brand-900 text-white">
      <header className="flex items-center justify-between px-5 py-4 text-sm text-white/70">
        <span>
          {info?.jobTitle} · {info?.company}
        </span>
        <span className="tabular-nums">
          {Math.floor(elapsed / 60000)}:{String(Math.floor(elapsed / 1000) % 60).padStart(2, '0')}
        </span>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-5">
        <div className="relative flex h-44 w-44 items-center justify-center">
          {speaking && <span className="pulse-ring absolute inset-0 rounded-full bg-brand-500/40" />}
          {listening && !textMode && (
            <span className="absolute inset-0 rounded-full bg-emerald-400/20 transition-transform duration-75" style={{ transform: `scale(${1 + level * 0.35})` }} />
          )}
          <div className={cx('relative flex h-32 w-32 items-center justify-center rounded-full text-4xl font-semibold shadow-2xl', speaking ? 'bg-brand-500' : 'bg-white/10 ring-1 ring-white/20')}>
            {info?.interviewerName.slice(0, 1)}
          </div>
        </div>
        <p className="mt-4 text-sm text-white/60">
          {phase === 'connecting' || roomState === 'connecting'
            ? 'Connecting…'
            : speaking
              ? `${info?.interviewerName} is speaking`
              : roomState === 'thinking'
                ? `${info?.interviewerName} is thinking…`
                : textMode
                  ? 'Type your answer below'
                  : muted
                    ? 'You are muted'
                    : 'Listening. Speak whenever you are ready'}
        </p>

        <div className="mt-8 w-full max-w-2xl space-y-4 text-center">
          {lastAi && <p className="text-xl leading-relaxed text-white/95">{lastAi.text}</p>}
          {lastYou && <p className="text-sm text-white/50">You: {lastYou.text}</p>}
          {message && <p className="rounded-lg bg-white/10 px-4 py-2 text-sm">{message}</p>}
        </div>
      </main>

      <footer className="px-5 pb-8">
        {textMode && (
          <form
            className="mx-auto mb-4 flex max-w-2xl gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submitText();
            }}
          >
            <input
              className="flex-1 rounded-lg border-0 bg-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:ring-2 focus:ring-brand-500"
              placeholder="Type your answer…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPaste={() => send({ type: 'integrity', event: 'paste' })}
              autoFocus
            />
            <Button variant="primary" type="submit">
              Send
            </Button>
          </form>
        )}
        <div className="flex justify-center gap-3">
          {!textMode && (
            <button
              className={cx('rounded-full px-5 py-3 text-sm font-medium ring-1', muted ? 'bg-amber-500 text-white ring-amber-400' : 'bg-white/10 ring-white/20 hover:bg-white/20')}
              onClick={() => {
                const next = !muted;
                setMuted(next);
                mic.current?.setMuted(next);
                send({ type: 'integrity', event: next ? 'mic_muted' : 'mic_unmuted' });
              }}
            >
              {muted ? 'Unmute' : 'Mute'}
            </button>
          )}
          <button className="rounded-full bg-rose-600 px-5 py-3 text-sm font-medium hover:bg-rose-500" onClick={endInterview}>
            End interview
          </button>
        </div>
      </footer>
    </div>
  );
}

function mergeCaption(list: Caption[], next: Caption): Caption[] {
  const last = list.at(-1);
  // Sentences of one AI turn arrive separately; show them as one caption.
  if (last && last.who === 'ai' && last.turn === next.turn) return [...list.slice(0, -1), { ...last, text: `${last.text} ${next.text}` }];
  return [...list, next].slice(-12);
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-xl rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:p-8">
        <div className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-500">
          <img src="/favicon.svg" alt="" className="h-6 w-6" /> Eduvy
        </div>
        {children}
      </div>
    </div>
  );
}

function Tip({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </li>
  );
}
